/**
 * AssistantChat — the shared chef-assistant window.
 *
 * Text only: no microphone, no audio, no voice. Two chefs, one window, one
 * engine; switching chef starts that chef's own conversation and carries
 * nothing over. Every reply carries
 * no label about how the chef is built (the owner removed every visible mention
 * of it; the honest answer lives in the chef's own words, in `are_you_real`), and the chef
 * never sees (and the screen never implies) what the household already has.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    ArrowLeft, BookOpen, ChefHat, MessageSquarePlus, MoreVertical, Send, Sparkles,
    Trash2, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SheetLayer } from '../../lib/phoneSheet';
import RecipeCard from './RecipeCard';

const PersonaTile = ({ p, onPick }) => {
    const { t } = useTranslation();
    return (
        <button onClick={() => onPick(p.key)} data-testid={`chef-persona-${p.key}`}
            className="flex w-full items-center gap-4 rounded-3xl border-2 border-slate-200 p-4 text-left transition-colors hover:border-[#13265C] dark:border-slate-700">
            <img src={p.avatar} alt="" aria-hidden="true" className="h-16 w-16 shrink-0" />
            <span className="min-w-0 flex-1">
                <span className="block text-lg font-black leading-tight text-foreground">{p.name}</span>
                <span className="block text-base text-muted-foreground">
                    {t(p.speciality === 'baking' ? 'askChef.bakingHelper' : 'askChef.cookingHelper')}
                </span>
            </span>
        </button>
    );
};

/** The waiting state: the chef's own avatar and a short line that rotates.
 *  Never a spinner with the word "Thinking", never a blocker for a tap. */
const WaitingFor = ({ person }) => {
    const { t } = useTranslation();
    const calm = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const [i, setI] = useState(0);
    useEffect(() => {
        if (calm) return undefined;
        const id = setInterval(() => setI((n) => n + 1), 2600);
        return () => clearInterval(id);
    }, [calm]);
    const name = person?.name || '';
    const line = t(`askChef.waiting${(i % 3) + 1}`, { name });
    return (
        <div className="pointer-events-none flex items-center gap-2" data-testid="chef-thinking">
            {person?.avatar ? (
                <img src={person.avatar} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
            ) : null}
            <span className="flex items-center gap-2 rounded-3xl rounded-tl-md bg-slate-100 px-4 py-2.5 text-sm italic text-muted-foreground dark:bg-slate-800"
                data-testid="chef-waiting-line">
                {line}
                <span aria-hidden className={`h-1.5 w-1.5 rounded-full bg-slate-400 ${calm ? '' : 'motion-safe:animate-pulse'}`} />
            </span>
        </div>
    );
};

export default function AssistantChat({ api, config, lang, onClose, onAdded, convo }) {
    const { t } = useTranslation();
    const { persona, setPersona, messages, setMessages, sending, left,
        send, startWith, newChat } = convo;
    const [input, setInput] = useState('');
    const [menu, setMenu] = useState(false);
    const [view, setView] = useState('chat');
    const [recipes, setRecipes] = useState([]);
    const [noSuggestions, setNoSuggestions] = useState(!!config.no_suggestions);
    const [saving, setSaving] = useState(false);
    const endRef = useRef(null);

    const people = config.personas || [];
    const current = people.find((p) => p.key === persona) || null;

    /** Nothing a shopper taps may fail in silence. */
    const guard = async (fn) => {
        try { await fn(); } catch (e) { toast.error(t('askChef.failed')); }
    };

    useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, view]);

    const patchMessage = (idx, patch) => setMessages(
        (m) => m.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

    const scale = (idx, servings) => guard(async () => {
        const row = messages[idx];
        const r = await api('/recipes/scale', { method: 'POST',
            body: { recipe: row.recipe, servings, lang } });
        patchMessage(idx, { recipe: { ...row.recipe, ...r } });
    });

    const addRows = async (rows, opts = {}) => {
        try {
            const r = await api('/add-to-list', { method: 'POST',
                body: { items: rows, lang, suggestion_id: opts.suggestionId || null } });
            // ONE confirmation, the host's: "Added · <name>" for two seconds. A
            // chef add must feel the same as tapping a food in the aisle. The
            // whole-recipe tap passes `quiet` because it says its own line.
            onAdded?.({ name: rows.length === 1 ? (rows[0].label || rows[0].name)
                : t('askChef.addedCount', { n: r.added_count }) },
                { silent: !!opts.quiet });
            return r;
        } catch (e) {
            toast.error(t('askChef.failed'));
            return null;                          // never a silent failure
        }
    };

    /** An ingredient the catalogue does not have: the SHOPPER adds it in the
     *  recipe's own words, and it is saved for this household from then on. */
    const addAsWritten = async (label, opts = {}) => {
        try {
            const r = await api('/add-as-written', { method: 'POST',
                body: { text: label, lang } });
            onAdded?.({ name: r.name }, { silent: !!opts.quiet });
            return r;
        } catch (e) {
            toast.error(t('askChef.failed'));
            return null;
        }
    };

    /** THE SPONSORED CARD WORKS WITH OR WITHOUT A RECIPE. With a recipe on
     *  screen the product joins that recipe's checklist (where the customer
     *  still chooses); with no recipe yet it goes straight on the shopping
     *  list and the chef says so in the chat. Either way the 'added'
     *  suggestion event is logged. */
    const addSuggestion = async (idx, s, hasRecipe) => {
        if (hasRecipe) {
            const m = messages[idx];
            patchMessage(idx, { extras: [
                ...(m.extras || []).filter((e) => e.catalog_item_id !== s.catalog_item_id),
                { ...s, quantity: 1, amount: '' }] });
            api(`/suggestions/${s.id}/event`, { method: 'POST', body: { event: 'added' } })
                .catch(() => {});
            return;
        }
        const r = await addRows([{ catalog_item_id: s.catalog_item_id, quantity: 1,
            label: s.name }], { suggestionId: s.id });
        if (!r) return;
        setMessages((m) => [...m, { role: 'chef', persona,
            text: t('askChef.suggestionAdded', { name: s.name }), suggestions: [],
            extras: [] }]);
    };

    const saveRecipe = (idx) => guard(async () => {
        setSaving(true);
        try {
            await api('/recipes', { method: 'POST',
                body: { recipe: messages[idx].recipe, persona, lang } });
            toast.success(t('askChef.saved'));
        } finally { setSaving(false); }
    });

    const openBox = () => guard(async () => {
        setMenu(false);
        const r = await api(`/recipes?lang=${lang}`);
        setRecipes(r.recipes || []);
        setView('box');
    });

    const toggleSuggestions = async () => {
        const next = !noSuggestions;
        setNoSuggestions(next);
        setMenu(false);
        try {
            await api('/preferences', { method: 'POST', body: { no_suggestions: next } });
            toast.success(next ? t('askChef.suggestionsOff') : t('askChef.suggestionsOn'));
        } catch (e) {
            setNoSuggestions(!next);              // the server never heard it
            toast.error(t('askChef.failed'));
        }
    };

    const deleteHistory = () => guard(async () => {
        setMenu(false);
        await api('/chats', { method: 'DELETE' });
        newChat();
        toast.success(t('askChef.historyDeleted'));
    });

    /** SWITCHING CHEF SWITCHES THE WHOLE CONVERSATION. A new chat, the new
     *  chef's own greeting, name, avatar and voice — nothing of the previous
     *  chef is carried over, from the chooser or from the hand-off button, and
     *  an answer still travelling for the previous chef is dropped. */
    const switchTo = (key) => {
        setView('chat');
        startWith(key);
    };

    return (
        <SheetLayer>
        {/* THE WINDOW BELONGS TO THE SCREEN, NOT TO THE LIST. It is rendered
            into document.body and sized to the viewport the shopper can see,
            so neither the browser's bottom toolbar nor the demo bar can take
            the typing row away from them. */}
        <div className="yot-sheet z-[80] flex flex-col bg-white dark:bg-slate-950 md:items-center md:justify-center md:bg-slate-900/60 md:p-6" data-grocery-sheet="true"
            data-testid="chef-panel">
            <div className="flex min-h-0 w-full flex-1 flex-col md:h-auto md:max-h-[88vh] md:max-w-2xl md:flex-none md:rounded-3xl md:bg-white md:shadow-2xl md:dark:bg-slate-950">
                {/* header */}
                <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-3 dark:border-slate-800">
                    {view !== 'chat' || persona ? (
                        <button data-testid="chef-back"
                            onClick={() => (view !== 'chat' ? setView('chat') : setPersona(null))}
                            className="flex min-h-[44px] min-w-[44px] items-center gap-1.5 rounded-full px-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            <ArrowLeft className="h-5 w-5" />{t('askChef.back')}
                        </button>
                    ) : <span className="w-2" />}
                    <div className="flex min-w-0 flex-1 items-center justify-center gap-2 px-1">
                        {current ? <img src={current.avatar} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" /> : null}
                        <span className="min-w-0">
                            <span className="block text-sm font-bold leading-tight text-foreground sm:text-base"
                                data-testid="chef-header-title">
                                {view === 'box' ? t('askChef.recipeBox') : (current?.name || t('askChef.cardTitle'))}
                            </span>
                            {/* No badge, no label about how the chef is made.
                                The honest answer lives in the chef's own words
                                if a customer sincerely asks. */}
                        </span>
                    </div>
                    <div className="relative shrink-0">
                        <button onClick={() => setMenu((v) => !v)} data-testid="chef-menu-btn"
                            aria-label={t('askChef.menu')}
                            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                            <MoreVertical className="h-5 w-5" />
                        </button>
                        {menu ? (
                            <div className="absolute right-0 top-12 z-10 w-60 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900"
                                data-testid="chef-menu">
                                <button onClick={toggleSuggestions} data-testid="chef-no-suggestions"
                                    className="flex min-h-[48px] w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <Sparkles className="h-4 w-4" />
                                    {noSuggestions ? t('askChef.suggestionsOn') : t('askChef.noSuggestions')}
                                </button>
                                <button onClick={openBox} data-testid="chef-open-recipe-box"
                                    className="flex min-h-[48px] w-full items-center gap-2 px-3 text-left text-sm font-semibold text-foreground hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <BookOpen className="h-4 w-4" />{t('askChef.recipeBox')}
                                </button>
                                <button onClick={deleteHistory} data-testid="chef-delete-history"
                                    className="flex min-h-[48px] w-full items-center gap-2 px-3 text-left text-sm font-semibold text-rose-500 hover:bg-slate-50 dark:hover:bg-slate-800">
                                    <Trash2 className="h-4 w-4" />{t('askChef.deleteHistory')}
                                </button>
                            </div>
                        ) : null}
                    </div>
                    <button onClick={onClose} data-testid="chef-close" aria-label={t('askChef.close')}
                        className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {/* body */}
                <div className={`min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pt-4 ${
                    persona && view === 'chat' ? 'pb-4' : 'yot-sheet-scroll'}`}>
                    {view === 'box' ? (
                        <div className="space-y-3" data-testid="chef-recipe-box">
                            {recipes.length === 0 ? (
                                <p className="text-sm text-muted-foreground" data-testid="chef-box-empty">
                                    {t('askChef.emptyBox')}
                                </p>
                            ) : recipes.map((r) => (
                                <div key={r.id} data-testid={`chef-saved-${r.id}`}>
                                    <RecipeCard recipe={r} canAdd={config.can_add} kid={config.kid_view}
                                        storeName={config.store_name}
                                        onScale={() => {}} onAdd={addRows}
                                        onAddAsWritten={addAsWritten}
                                        testId={`chef-saved-card-${r.id}`} />
                                    <button data-testid={`chef-saved-delete-${r.id}`}
                                        onClick={async () => {
                                            await api(`/recipes/${r.id}`, { method: 'DELETE' });
                                            setRecipes((rows) => rows.filter((x) => x.id !== r.id));
                                        }}
                                        className="mt-1 min-h-[44px] text-sm font-semibold text-rose-500">
                                        {t('askChef.deleteRecipe')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : !persona ? (
                        <div className="space-y-4" data-testid="chef-persona-chooser">
                            <p className="text-base font-bold text-foreground">{t('askChef.chooseTitle')}</p>
                            {people.map((p) => <PersonaTile key={p.key} p={p} onPick={switchTo} />)}
                        </div>
                    ) : (
                        <>
                            {messages.map((m, idx) => (
                                <div key={idx} className={m.role === 'customer' ? 'flex justify-end' : 'space-y-3'}
                                    data-testid={`chef-msg-${idx}`}>
                                    {m.role === 'customer' ? (
                                        <p className="max-w-[85%] rounded-3xl rounded-br-md bg-[#13265C] px-4 py-2.5 text-sm font-medium text-white">
                                            {m.text}
                                        </p>
                                    ) : (
                                        <>
                                            <div className="flex gap-2">
                                                <img src={(people.find((p) => p.key === m.persona) || current)?.avatar}
                                                    alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
                                                <div className="min-w-0 flex-1 space-y-1">
                                                    <p className="whitespace-pre-wrap rounded-3xl rounded-tl-md bg-slate-100 px-4 py-2.5 text-sm text-foreground dark:bg-slate-800">
                                                        {m.text}
                                                    </p>
                                                    {m.opinionTag ? (
                                                        <span className="mr-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200"
                                                            data-testid={`chef-opinion-tag-${idx}`}>
                                                            {t('askChef.opinionTag')}
                                                        </span>
                                                    ) : null}
                                                    {m.source === 'cache' || m.source === 'saved' ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-600"
                                                            data-testid={`chef-free-${idx}`}>
                                                            {t('askChef.alreadyKnows')}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>

                                            {m.recipe ? (
                                                <RecipeCard recipe={m.recipe} canAdd={config.can_add}
                                                    kid={config.kid_view} extras={m.extras || []}
                                                    storeName={config.store_name} saving={saving}
                                                    onScale={(s) => scale(idx, s)}
                                                    onAdd={(rows, opts) => addRows(rows, opts)}
                                                    onAddAsWritten={addAsWritten}
                                                    onSave={() => saveRecipe(idx)}
                                                    testId={`chef-recipe-${idx}`} />
                                            ) : null}

                                            {(m.knownRecipes || []).length ? (
                                                <div className="space-y-1" data-testid={`chef-known-${idx}`}>
                                                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                                        {t('askChef.knownRecipes')}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {m.knownRecipes.slice(0, 10).map((title) => (
                                                            <button key={title} onClick={() => send(title, { asRecipe: true })}
                                                                disabled={sending}
                                                                data-testid={`chef-known-chip-${title}`}
                                                                className="min-h-[44px] rounded-full border border-slate-300 px-3 text-sm font-semibold text-foreground disabled:opacity-50 dark:border-slate-600">
                                                                {title}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : null}

                                            {/* Cross-sell: AFTER the answer, in its own row, never the winner
                                                of a comparison, always labelled when it is paid for. */}
                                            {!noSuggestions && (m.suggestions || []).length ? (
                                                <div className="space-y-2 rounded-2xl border border-dashed border-slate-300 p-3 dark:border-slate-700"
                                                    data-testid={`chef-suggestions-${idx}`}>
                                                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                                        {t('askChef.makeItBetter')}
                                                    </p>
                                                    {m.suggestions.map((s, n) => (
                                                        // The same placement can
                                                        // appear in two different
                                                        // answers, so the key
                                                        // carries the message too.
                                                        <button key={`${idx}-${s.id}-${n}`} type="button"
                                                            disabled={sending}
                                                            onClick={() => addSuggestion(idx, s, !!m.recipe)}
                                                            className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-1 text-left transition-colors hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-800/60"
                                                            data-testid={`chef-suggestion-${idx}-${s.catalog_item_id}`}>
                                                            {s.icon ? <img src={s.icon} alt="" aria-hidden="true" className="h-7 w-7" /> : null}
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block text-sm font-semibold text-foreground">
                                                                    {s.name}
                                                                    {s.brand_label ? ` · ${s.brand_label}` : ''}
                                                                </span>
                                                                <span className="block text-[11px] text-muted-foreground">{s.line}</span>
                                                            </span>
                                                            <span data-testid={`chef-suggestion-label-${idx}-${s.catalog_item_id}`}
                                                                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                                                                    s.sponsored
                                                                        ? 'bg-[#F58220] text-[#13265C]'
                                                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                                                                {s.sponsored ? t('askChef.sponsored') : t('askChef.sample')}
                                                            </span>
                                                            {/* ALWAYS THERE. With a recipe on screen it joins the
                                                                checklist; without one it goes straight on the list. */}
                                                            <span data-testid={`chef-suggestion-add-${idx}-${s.catalog_item_id}`}
                                                                className="min-h-[44px] shrink-0 inline-flex items-center rounded-full bg-slate-900 px-3 text-xs font-bold text-white dark:bg-white dark:text-slate-900">
                                                                {m.recipe ? t('askChef.addToChecklist') : t('askChef.addToList')}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : null}

                                            {m.handoff ? (
                                                <button onClick={() => switchTo(m.handoff.to)}
                                                    data-testid={`chef-handoff-${m.handoff.to}`}
                                                    className="flex min-h-[52px] w-full items-center gap-2 rounded-2xl border-2 border-[#13265C] px-3 text-left text-sm font-bold text-[#13265C] dark:border-white dark:text-white">
                                                    <img src={m.handoff.avatar} alt="" aria-hidden="true" className="h-8 w-8" />
                                                    {t('askChef.switchTo', { name: m.handoff.name })}
                                                </button>
                                            ) : null}

                                            {(m.ideas || []).length ? (
                                                <div className="flex flex-col gap-2" data-testid={`chef-ideas-${idx}`}>
                                                    {m.ideas.map((dish) => (
                                                        <button key={dish} onClick={() => send(dish, { asRecipe: true })}
                                                            disabled={sending}
                                                            data-testid={`chef-idea-${dish}`}
                                                            className="flex min-h-[44px] items-center justify-between gap-2 rounded-2xl border-2 border-[#13265C] px-4 text-left text-sm font-bold text-[#13265C] transition-colors active:bg-[#13265C]/10 disabled:opacity-50 dark:border-white dark:text-white dark:active:bg-white/10">
                                                            {dish}
                                                            <ChefHat className="h-4 w-4 shrink-0" />
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            ))}
                            {sending ? <WaitingFor person={current} /> : null}
                            <div ref={endRef} />
                        </>
                    )}
                </div>

                {/* footer */}
                {persona && view === 'chat' ? (
                    <div className="yot-sheet-bottom shrink-0 space-y-2 border-t border-slate-200 px-3 pt-3 dark:border-slate-800"
                        data-testid="chef-footer">
                        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-muted-foreground">
                            {/* The counter exists ONLY when the grocer set a limit. */}
                            {typeof left === 'number' ? (
                                <span data-testid="chef-questions-left">
                                    {left > 0
                                        ? t('askChef.questionsLeft', { n: left })
                                        : t('askChef.noQuestionsLeft')}
                                </span>
                            ) : <span />}
                            <button onClick={newChat}
                                data-testid="chef-new-chat"
                                className="flex min-h-[32px] items-center gap-1 font-bold text-emerald-700 dark:text-emerald-300">
                                <MessageSquarePlus className="h-4 w-4" />{t('askChef.newChat')}
                            </button>
                        </div>
                        <form className="flex items-center gap-2"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const text = input;
                                setInput('');
                                send(text);
                            }}>
                            <Input value={input} onChange={(e) => setInput(e.target.value)}
                                placeholder={t('askChef.placeholder')} data-testid="chef-input"
                                className="min-h-[52px] text-base" />
                            <Button type="submit" disabled={sending || !input.trim()}
                                data-testid="chef-send" aria-label={t('askChef.send')}
                                className="min-h-[52px] min-w-[52px]">
                                <Send className="h-5 w-5" />
                            </Button>
                        </form>
                    </div>
                ) : null}
            </div>
        </div>
        </SheetLayer>
    );
}
